import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from './api.js';


  // ============ CONSTANTS ============
  const KINDS = [
    { id: 'idea',      label: 'Ideia',      color: 'var(--cyan)',    hint: 'Algo novo que surgiu' },
    { id: 'task',      label: 'Tarefa',     color: 'var(--amber)',   hint: 'Ação concreta' },
    { id: 'milestone', label: 'Marco',      color: 'var(--lime)',    hint: 'Entrega ou ponto de controle' },
    { id: 'blocker',   label: 'Bloqueio',   color: 'var(--danger)',  hint: 'Algo bloqueando o avanço' },
    { id: 'resource',  label: 'Recurso',    color: 'var(--magenta)', hint: 'Link, arquivo ou referência' },
    { id: 'decision',  label: 'Decisão',    color: '#b28cff',        hint: 'Escolha com consequência' },
  ];

  const STATUSES = [
    { id: 'idle',    label: 'Parado' },
    { id: 'doing',   label: 'Fazendo' },
    { id: 'blocked', label: 'Travado' },
    { id: 'done',    label: 'Pronto' },
  ];

  const PERSONAS = {
    strategic:   { name: 'S: Workflow Architect',   role: 'Estratégico, sistemas',     tone: 'pensa em sistemas, conexões e gargalos. Fala direto.' },
    pragmatic:   { name: 'S: Execution Coach',      role: 'Pragmático, direto',        tone: 'foca em próximo passo concreto. Corta filosofia.' },
    socratic:    { name: 'S: Critic Partner',       role: 'Socrático, perguntas',      tone: 'faz perguntas afiadas para destravar o pensamento.' },
    motivational:{ name: 'S: Momentum Keeper',      role: 'Motivacional, ritmo',       tone: 'celebra pequenas vitórias, mantém o ritmo.' },
  };

  // ============ STORAGE ============
  // Dados do plano (norte/nodes/edges) vêm do backend via window.__bootstrap,
  // preenchido pelo main.tsx antes de montar o App. UI prefs ficam em
  // localStorage per-device.
  const UI_KEY = 'fluxo_ui_prefs_v1';

  const loadUiPrefs = () => {
    try {
      const raw = localStorage.getItem(UI_KEY);
      if (raw) return JSON.parse(raw);
    } catch (e) {}
    return {};
  };

  const parseNorte = (raw) => {
    if (!raw) return null;
    try {
      const p = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return typeof p === 'string' ? { objective: p } : p;
    } catch {
      return { objective: String(raw) };
    }
  };

  const loadState = () => {
    const boot = typeof window !== 'undefined' ? window.__bootstrap : null;
    const ui = loadUiPrefs();
    return {
      planId: boot?.plan?.id ?? null,
      norte: parseNorte(boot?.plan?.norte),
      nodes: boot?.nodes ?? [],
      edges: boot?.edges ?? [],
      pan: ui.pan ?? { x: 0, y: 0 },
      zoom: ui.zoom ?? 1,
      focusMode: ui.focusMode ?? false,
      messages: ui.messages ?? [],
      onboarded: ui.onboarded ?? (boot?.nodes?.length > 0),
      nudge: ui.nudge ?? defaultNudge(),
    };
  };

  const saveUiPrefs = (state) => {
    const ui = {
      pan: state.pan, zoom: state.zoom, focusMode: state.focusMode,
      onboarded: state.onboarded, nudge: state.nudge, messages: state.messages,
    };
    try { localStorage.setItem(UI_KEY, JSON.stringify(ui)); } catch (e) {}
  };

  const diffAndSync = (state, prev, wsState) => {
    const ws = typeof window !== 'undefined' ? window.__bootstrap?.ws : null;
    if (!ws) return;
    const prevNodes = new Map((prev?.nodes || []).map(n => [n.id, n]));
    const newNodes = new Map((state.nodes || []).map(n => [n.id, n]));
    for (const [id, node] of newNodes) {
      // Skip nodes that just arrived from WS — re-sending creates ping-pong loop
      const wsSynced = wsState?.nodes?.get(id);
      if (wsSynced && JSON.stringify(wsSynced) === JSON.stringify(node)) continue;
      const p = prevNodes.get(id);
      if (!p || JSON.stringify(p) !== JSON.stringify(node)) ws.send({ t: 'node.upsert', node });
    }
    for (const id of prevNodes.keys()) {
      if (!newNodes.has(id)) ws.send({ t: 'node.delete', id });
    }
    const prevEdges = new Map((prev?.edges || []).map(e => [e.id, e]));
    const newEdges = new Map((state.edges || []).map(e => [e.id, e]));
    for (const [id, edge] of newEdges) {
      const wsSynced = wsState?.edges?.get(id);
      if (wsSynced && JSON.stringify(wsSynced) === JSON.stringify(edge)) continue;
      const p = prevEdges.get(id);
      if (!p || JSON.stringify(p) !== JSON.stringify(edge)) ws.send({ t: 'edge.upsert', edge });
    }
    for (const id of prevEdges.keys()) {
      if (!newEdges.has(id)) ws.send({ t: 'edge.delete', id });
    }

    const prevNorteStr = JSON.stringify(prev?.norte ?? null);
    const newNorteStr = JSON.stringify(state.norte ?? null);
    if (prevNorteStr !== newNorteStr && state.planId) {
      api.updatePlan(state.planId, { norte: state.norte ? JSON.stringify(state.norte) : null }).catch(() => {});
    }
  };

  const saveState = (state, prev, wsState) => {
    saveUiPrefs(state);
    diffAndSync(state, prev, wsState);
  };

  function emptyState() {
    return {
      norte: null,
      nodes: [],
      edges: [],
      pan: { x: 0, y: 0 },
      zoom: 1,
      focusMode: false,
      messages: [],
      onboarded: false,
      nudge: defaultNudge(),
    };
  }
  function defaultNudge() {
    return {
      enabled: false,
      intervalMin: 30,
      mode: 'soft', // soft | firm | brutal
      silentStart: 22, // hora (0-23)
      silentEnd: 9,
      lastFiredAt: null,
      focusNodeId: null,
    };
  }

  function seedPLX() {
    // Plano WhatsApp SaaS 30d extraído do PLX-Empire-Infra-Complete.md
    const mk = (id, kind, title, desc, x, y, extras = {}) => ({
      id, kind, title, desc, x, y,
      status: 'idle', tag: extras.tag || '', progress: 0, checks: [],
      dod: extras.dod || [], critical: extras.critical !== false, notes: extras.notes || '',
    });
    return {
      norte: {
        objective: 'Primeiro R$297 MRR via WhatsApp SaaS',
        horizon: '30 dias',
        dod: [
          { text: 'Landing no ar em whats.playloadx.ai', done: false },
          { text: '50 DMs enviadas no LinkedIn', done: false },
          { text: '10 demos agendadas', done: false },
          { text: '1 cliente pagante (R$297 na conta)', done: false },
        ],
        createdAt: Date.now(),
      },
      nodes: [
        mk('nt',  'milestone', '🎯 NORTE · R$297 MRR em 30d',        'Primeiro cliente pagante no stack WhatsApp (Chatwoot + Evolution).', 580, 80,  { tag: 'meta', critical: true, dod: [{text:'R$297 confirmado',done:false}] }),

        mk('s1',  'milestone', 'S1 · Landing page no ar',            'Hero + 3 features + pricing + CTA trial. whats.playloadx.ai', 220, 240, { tag: 'sem1', critical: true, dod: [
          {text:'Copy do hero aprovada', done:false},
          {text:'3 features descritas', done:false},
          {text:'Pricing R$297/697/1497', done:false},
          {text:'Deploy em CF Pages', done:false},
          {text:'Domain whats.playloadx.ai', done:false},
        ]}),
        mk('s1a', 'task',      'Escrever copy do hero',              'Proposta: "WhatsApp CRM automatizado pra PMEs BR"', 40, 420, { tag:'copy', critical: true, dod:[{text:'título+subtítulo+CTA',done:false}] }),
        mk('s1b', 'task',      'Mockup landing no Figma',            'Wireframe primeiro, depois hi-fi.', 220, 420, { tag:'design', critical: true, dod:[{text:'Wireframe',done:false},{text:'Hi-fi',done:false}] }),
        mk('s1c', 'task',      'Deploy CF Pages + domínio',          'Build estático, conectar whats.playloadx.ai', 400, 420, { tag:'dev', critical: true, dod:[{text:'Site 200 OK',done:false},{text:'HTTPS ativo',done:false}] }),

        mk('s2',  'milestone', 'S2 · Onboarding mínimo',             'Fluxo: cliente conecta WhatsApp → workspace Chatwoot → bot saudação.', 760, 240, { tag: 'sem2', critical: true, dod: [
          {text:'Evolution API conectada', done:false},
          {text:'Workspace Chatwoot criado por cliente', done:false},
          {text:'Bot saudação + FAQ configurado', done:false},
          {text:'Trial 14 dias habilitado', done:false},
        ]}),
        mk('s2a', 'task',      'Script de provisionamento cliente',  'Cria workspace CW isolado, associa instance Evolution.', 660, 420, { tag:'dev', critical: true }),
        mk('s2b', 'task',      'Template bot saudação + FAQ',        'Salesbot com 5 intents iniciais.', 860, 420, { tag:'content', critical: true }),

        mk('s3',  'milestone', 'S3 · Outbound manual',               '50 DMs LinkedIn pra donos de ecommerce BR.', 1240, 240, { tag: 'sem3', critical: true, dod: [
          {text:'Lista 100 prospects', done:false},
          {text:'Template de DM aprovado', done:false},
          {text:'50 DMs enviadas', done:false},
          {text:'10 demos agendadas', done:false},
          {text:'2-3 trials assinados', done:false},
        ]}),
        mk('s3a', 'task',      'Montar lista de 100 prospects',      'Ecommerce BR Shopify/VTEX, 10-50 funcionários.', 1120, 420, { tag:'sales', critical: true }),
        mk('s3b', 'task',      'Escrever template DM LinkedIn',      'Hook + dor + oferta + CTA em 4 linhas.', 1320, 420, { tag:'copy', critical: true }),

        mk('s4',  'milestone', 'S4 · Conversão → Primeiro R$',       'Follow-up dos trials. Meta: 1 paid.', 1680, 240, { tag: 'sem4', critical: true, dod: [
          {text:'Cobrança via Pix/Stripe ativa', done:false},
          {text:'1 fatura paga de R$297', done:false},
        ]}),

        mk('gap1','resource',  'Obsidian vault (notes)',             'Segundo-cérebro dia 1 do setup produtividade.', 1680, 540, { tag:'pessoal', critical: false }),
        mk('gap2','resource',  'Todoist ou Things 3',                'Task mgmt dia 2.', 1680, 660, { tag:'pessoal', critical: false }),

        mk('risk','blocker',   'Risco · Burnout solo founder',       'Hire antes de 20 clientes. Automação no-code.', 40, 80, { tag:'risco', critical: false }),
      ],
      edges: [
        { id: 'e1', from: 's1',  to: 'nt',  fromSide: 'top', toSide: 'bottom' },
        { id: 'e2', from: 's2',  to: 'nt',  fromSide: 'top', toSide: 'bottom' },
        { id: 'e3', from: 's3',  to: 'nt',  fromSide: 'top', toSide: 'bottom' },
        { id: 'e4', from: 's4',  to: 'nt',  fromSide: 'top', toSide: 'bottom' },
        { id: 'e5', from: 's1',  to: 's2',  fromSide: 'right', toSide: 'left' },
        { id: 'e6', from: 's2',  to: 's3',  fromSide: 'right', toSide: 'left' },
        { id: 'e7', from: 's3',  to: 's4',  fromSide: 'right', toSide: 'left' },
        { id: 'e8', from: 's1a', to: 's1',  fromSide: 'top', toSide: 'bottom' },
        { id: 'e9', from: 's1b', to: 's1',  fromSide: 'top', toSide: 'bottom' },
        { id: 'e10',from: 's1c', to: 's1',  fromSide: 'top', toSide: 'bottom' },
        { id: 'e11',from: 's2a', to: 's2',  fromSide: 'top', toSide: 'bottom' },
        { id: 'e12',from: 's2b', to: 's2',  fromSide: 'top', toSide: 'bottom' },
        { id: 'e13',from: 's3a', to: 's3',  fromSide: 'top', toSide: 'bottom' },
        { id: 'e14',from: 's3b', to: 's3',  fromSide: 'top', toSide: 'bottom' },
      ],
      pan: { x: 0, y: 0 },
      zoom: 0.75,
      focusMode: false,
      onboarded: true,
      nudge: defaultNudge(),
      messages: [
        { role: 'agent', text: 'Seed <strong>PLX Empire</strong> carregado. Seu Norte: <code>R$297 MRR em 30d via WhatsApp SaaS</code>. Caminho crítico S1→S2→S3→S4. Comece por <strong>S1 · Landing page</strong> é o que destrava todo o resto.', suggestions: [
          { icon: '→', text: 'Qual o próximo passo concreto agora (próximas 2h)?' },
          { icon: '⚠', text: 'Detectar loops ou redundâncias no meu fluxo' },
          { icon: '◎', text: 'Analisar se algum nó atual te afasta do Norte' },
          { icon: '◇', text: 'Entrar em modo Foco só caminho crítico' },
        ]},
      ],
    };
  }

  // ============ APP ============
  function App() {
    const [state, setState] = useState(loadState);
    const [selectedId, setSelectedId] = useState(null);
    const [selectedEdgeId, setSelectedEdgeId] = useState(null);
    const [hoveredNodeId, setHoveredNodeId] = useState(null);
    const [connecting, setConnecting] = useState(null); // { from, fromSide, x, y }
    const [agentTab, setAgentTab] = useState('chat');
    const [agentBusy, setAgentBusy] = useState(false);
    const [toast, setToast] = useState(null);
    const [tweaksOpen, setTweaksOpen] = useState(false);
    const [tweaks, setTweaks] = useState(() => window.__TWEAK_DEFAULTS || {});
    const [ctxMenu, setCtxMenu] = useState(null);
    const [showOnboarding, setShowOnboarding] = useState(() => !loadState().onboarded);
    const [showImport, setShowImport] = useState(false);
    const [showNorteEdit, setShowNorteEdit] = useState(false);
    const [nextStepId, setNextStepId] = useState(null);
    const [completionPrompt, setCompletionPrompt] = useState(null); // {nodeId}
    const [showNudgePanel, setShowNudgePanel] = useState(false);
    const [nudgeAlert, setNudgeAlert] = useState(null); // { mode, message, focusNode }
    const canvasWrapRef = useRef(null);

    // aplica tema no <html>
    useEffect(() => {
      document.documentElement.setAttribute('data-theme', tweaks.theme || 'obsidian');
    }, [tweaks.theme]);

    // persist state: UI prefs -> localStorage, plan state -> diff+WS
    const prevStateRef = useRef(state);
    // Tracks last state received from WS per node/edge — prevents ping-pong echo loop
    const wsStateRef = useRef({ nodes: new Map(), edges: new Map() });
    useEffect(() => {
      saveState(state, prevStateRef.current, wsStateRef.current);
      prevStateRef.current = state;
    }, [state]);

    // receber updates dos outros users via WS (handlers atachados ao ws handle)
    useEffect(() => {
      const ws = typeof window !== 'undefined' ? window.__bootstrap?.ws : null;
      if (!ws) return;
      ws.onNodeUpsert = (node) => {
        wsStateRef.current.nodes.set(node.id, node);
        setState(s => {
          const nodes = s.nodes.filter(n => n.id !== node.id);
          nodes.push(node);
          return { ...s, nodes };
        });
      };
      ws.onNodeDelete = (id) => {
        wsStateRef.current.nodes.delete(id);
        setState(s => ({ ...s, nodes: s.nodes.filter(n => n.id !== id) }));
      };
      ws.onEdgeUpsert = (edge) => {
        wsStateRef.current.edges.set(edge.id, edge);
        setState(s => {
          const edges = s.edges.filter(e => e.id !== edge.id);
          edges.push(edge);
          return { ...s, edges };
        });
      };
      ws.onEdgeDelete = (id) => {
        wsStateRef.current.edges.delete(id);
        setState(s => ({ ...s, edges: s.edges.filter(e => e.id !== id) }));
      };
      return () => {
        ws.onNodeUpsert = null; ws.onNodeDelete = null;
        ws.onEdgeUpsert = null; ws.onEdgeDelete = null;
      };
    }, []);

    // ---- toast helper ----
    const flash = (msg) => { setToast(msg); clearTimeout(flash._t); flash._t = setTimeout(() => setToast(null), 2000); };

    // ---- selected node ----
    const selectedNode = state.nodes.find(n => n.id === selectedId);

    // ---- node ops ----
    const addNode = useCallback((kind, pos) => {
      const id = 'n' + Date.now().toString(36) + Math.random().toString(36).slice(2, 5);
      const node = {
        id, kind: kind || 'task',
        title: kind === 'idea' ? 'Nova ideia' : kind === 'milestone' ? 'Novo marco' : kind === 'blocker' ? 'Novo bloqueio' : kind === 'resource' ? 'Novo recurso' : kind === 'decision' ? 'Nova decisão' : 'Nova tarefa',
        desc: '',
        x: pos?.x ?? 400, y: pos?.y ?? 200,
        status: 'idle', tag: '', progress: 0, checks: [],
      };
      setState(s => ({ ...s, nodes: [...s.nodes, node] }));
      setSelectedId(id);
      return id;
    }, []);

    const updateNode = useCallback((id, patch) => {
      setState(s => {
        const next = s.nodes.map(n => {
          if (n.id !== id) return n;
          const updated = { ...n, ...(typeof patch === 'function' ? patch(n) : patch) };
          // Auto: se todos DoDs ✓ E status != done E tem ao menos 1 DoD → trigger prompt
          const dod = updated.dod || [];
          const allDodDone = dod.length > 0 && dod.every(d => d.done);
          const wasAllDone = (n.dod || []).length > 0 && (n.dod || []).every(d => d.done);
          if (allDodDone && !wasAllDone && updated.status !== 'done') {
            // schedule prompt (out of setState)
            queueMicrotask(() => setCompletionPrompt({ nodeId: id }));
          }
          // Inverso: se desmarcou DoD e estava done → volta pra doing
          const someDodOpen = dod.length > 0 && dod.some(d => !d.done);
          if (someDodOpen && n.status === 'done' && updated.status === 'done') {
            updated.status = 'doing';
            queueMicrotask(() => flash(`"${updated.title}" voltou para Fazendo DoD reaberto`));
          }
          return updated;
        });
        return { ...s, nodes: next };
      });
    }, []);

    const deleteNode = useCallback((id) => {
      setState(s => ({
        ...s,
        nodes: s.nodes.filter(n => n.id !== id),
        edges: s.edges.filter(e => e.from !== id && e.to !== id),
      }));
      if (selectedId === id) setSelectedId(null);
    }, [selectedId]);

    const addEdge = useCallback((from, to, fromSide, toSide) => {
      if (from === to) return;
      setState(s => {
        if (s.edges.some(e => e.from === from && e.to === to)) return s;
        const id = 'e' + Date.now().toString(36);
        return { ...s, edges: [...s.edges, { id, from, to, fromSide: fromSide || 'right', toSide: toSide || 'left' }] };
      });
    }, []);

    const deleteEdge = useCallback((id) => {
      setState(s => ({ ...s, edges: s.edges.filter(e => e.id !== id) }));
      if (selectedEdgeId === id) setSelectedEdgeId(null);
    }, [selectedEdgeId]);

    // ---- pan/zoom ----
    const onWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = -e.deltaY * 0.002;
        setState(s => {
          const nz = Math.max(0.4, Math.min(2.2, s.zoom + delta));
          return { ...s, zoom: nz };
        });
      } else {
        setState(s => ({ ...s, pan: { x: s.pan.x - e.deltaX, y: s.pan.y - e.deltaY } }));
      }
    };

    const [panning, setPanning] = useState(false);
    const panStart = useRef(null);
    const onCanvasMouseDown = (e) => {
      if (e.target.closest('.node') || e.target.closest('.edge-hit')) return;
      setSelectedId(null);
      setSelectedEdgeId(null);
      setPanning(true);
      panStart.current = { x: e.clientX, y: e.clientY, pan: { ...state.pan } };
    };

    useEffect(() => {
      if (!panning) return;
      const onMove = (e) => {
        const s = panStart.current;
        if (!s) return;
        setState(st => ({ ...st, pan: { x: s.pan.x + (e.clientX - s.x), y: s.pan.y + (e.clientY - s.y) } }));
      };
      const onUp = () => setPanning(false);
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    }, [panning]);

    // ---- node drag ----
    const dragState = useRef(null);
    const startNodeDrag = (e, id) => {
      if (e.target.closest('.node-handle') || e.target.closest('.node-status') || e.target.closest('.node-check')) return;
      e.stopPropagation();
      const node = state.nodes.find(n => n.id === id);
      dragState.current = { id, startX: e.clientX, startY: e.clientY, nodeX: node.x, nodeY: node.y };
      setSelectedId(id);
      setSelectedEdgeId(null);
    };
    useEffect(() => {
      const onMove = (e) => {
        if (!dragState.current) return;
        const d = dragState.current;
        const dx = (e.clientX - d.startX) / state.zoom;
        const dy = (e.clientY - d.startY) / state.zoom;
        updateNode(d.id, { x: d.nodeX + dx, y: d.nodeY + dy });
      };
      const onUp = () => { dragState.current = null; };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    }, [state.zoom, updateNode]);

    // ---- edge creation ----
    const startConnect = (e, nodeId, side) => {
      e.stopPropagation();
      const node = state.nodes.find(n => n.id === nodeId);
      const pt = handlePoint(node, side);
      setConnecting({ from: nodeId, fromSide: side, x: pt.x, y: pt.y });
    };
    useEffect(() => {
      if (!connecting) return;
      const onMove = (e) => {
        const rect = canvasWrapRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left - state.pan.x) / state.zoom;
        const y = (e.clientY - rect.top - state.pan.y) / state.zoom;
        setConnecting(c => ({ ...c, mx: x, my: y }));
      };
      const onUp = (e) => {
        const targetEl = document.elementFromPoint(e.clientX, e.clientY);
        const handle = targetEl?.closest('.node-handle');
        const nodeEl = targetEl?.closest('.node');
        if (handle && nodeEl) {
          const toId = nodeEl.dataset.id;
          const toSide = handle.dataset.side;
          addEdge(connecting.from, toId, connecting.fromSide, toSide);
        } else if (nodeEl) {
          const toId = nodeEl.dataset.id;
          addEdge(connecting.from, toId, connecting.fromSide, 'left');
        }
        setConnecting(null);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    }, [connecting, state.pan, state.zoom, addEdge]);

    // ---- drag-from-palette ----
    const [draggingKind, setDraggingKind] = useState(null);
    const onPaletteDragStart = (e, kind) => { setDraggingKind(kind); e.dataTransfer.effectAllowed = 'copy'; };
    const onCanvasDragOver = (e) => { e.preventDefault(); };
    const onCanvasDrop = (e) => {
      e.preventDefault();
      if (!draggingKind) return;
      const rect = canvasWrapRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left - state.pan.x) / state.zoom - 120;
      const y = (e.clientY - rect.top - state.pan.y) / state.zoom - 30;
      addNode(draggingKind, { x, y });
      setDraggingKind(null);
    };

    // ---- keyboard ----
    useEffect(() => {
      const onKey = (e) => {
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
        if (e.key === 'Delete' || e.key === 'Backspace') {
          if (selectedId) { deleteNode(selectedId); e.preventDefault(); }
          else if (selectedEdgeId) { deleteEdge(selectedEdgeId); e.preventDefault(); }
        }
        if (e.key === 'n' && !e.metaKey && !e.ctrlKey) {
          const rect = canvasWrapRef.current.getBoundingClientRect();
          const x = (rect.width / 2 - state.pan.x) / state.zoom - 120;
          const y = (rect.height / 2 - state.pan.y) / state.zoom - 30;
          addNode('task', { x, y });
        }
        if (e.key === 'Escape') { setSelectedId(null); setSelectedEdgeId(null); setConnecting(null); setCtxMenu(null); }
      };
      window.addEventListener('keydown', onKey);
      return () => window.removeEventListener('keydown', onKey);
    }, [selectedId, selectedEdgeId, state.pan, state.zoom, deleteNode, deleteEdge, addNode]);

    // ---- context menu ----
    const onCanvasContextMenu = (e) => {
      e.preventDefault();
      const rect = canvasWrapRef.current.getBoundingClientRect();
      const cx = (e.clientX - rect.left - state.pan.x) / state.zoom;
      const cy = (e.clientY - rect.top - state.pan.y) / state.zoom;
      setCtxMenu({ x: e.clientX, y: e.clientY, cx, cy });
    };

    // ---- agent ----
    const pushMessage = (m) => setState(s => ({ ...s, messages: [...s.messages, m] }));

    const runAgent = async (userText) => {
      const persona = PERSONAS[tweaks.agentPersona] || PERSONAS.strategic;
      setAgentBusy(true);
      pushMessage({ role: 'user', text: userText });

      const snapshot = summarizeState(state);
      const drift = detectDrift(state);
      const loops = detectLoops(state);
      const alertsTxt = [...drift, ...loops].map(a => `- ${a.text.replace(/<[^>]+>/g,'')}`).join('\n');
      const prompt = `Você é "${persona.name}" ${persona.role}. Tom: ${persona.tone}
Você é um arquiteto de workflow brutalmente honesto. Seu trabalho: manter o usuário no Norte e impedir drift, loops e re-início de coisas.

REGRAS:
- Português brasileiro, direto, no máximo 4 frases + lista curta.
- SEMPRE relacione qualquer pergunta ao Norte. Se a pergunta te afasta do caminho crítico, diga isso ABERTAMENTE antes de responder.
- Se o usuário pedir pra criar nó/tarefa fora do caminho crítico, pergunte: "isso te aproxima do Norte ou estamos parqueando pra depois?"
- Cite nomes dos nós entre aspas.
- HTML simples só: <strong>, <code>, <ul><li>.
- Termine com linha "SUGESTÕES:" + 2-4 sugestões separadas por " | ".

ESTADO DO CANVAS:
${snapshot}
${alertsTxt ? '\n⚠ ALERTAS AUTOMÁTICOS DETECTADOS:\n'+alertsTxt+'\n' : ''}
PERGUNTA: ${userText}`;

      try {
        const raw = await window.claude.complete(prompt);
        const { text, suggestions } = parseAgentReply(raw);
        pushMessage({ role: 'agent', text, suggestions });
      } catch (err) {
        pushMessage({ role: 'agent', text: 'Deu ruim pra chamar o modelo agora. Tenta de novo em alguns segundos.', suggestions: [] });
      } finally {
        setAgentBusy(false);
      }
    };

    // limpa halo "próximo passo" quando nó vira done
    useEffect(() => {
      if (nextStepId) {
        const n = state.nodes.find(x => x.id === nextStepId);
        if (!n || n.status === 'done') setNextStepId(null);
      }
    }, [state.nodes, nextStepId]);

    // ============ COBRANÇA ============
    const updateNudge = useCallback((patch) => {
      setState(s => ({ ...s, nudge: { ...(s.nudge || defaultNudge()), ...patch } }));
    }, []);

    const isInSilentWindow = (nudge) => {
      const h = new Date().getHours();
      const a = nudge.silentStart, b = nudge.silentEnd;
      if (a === b) return false;
      if (a < b) return h >= a && h < b;
      return h >= a || h < b; // crosses midnight
    };

    const fireNudge = useCallback(() => {
      const nudge = state.nudge || defaultNudge();
      const focus = nudge.focusNodeId ? state.nodes.find(n => n.id === nudge.focusNodeId) : null;
      const nextSugg = findNextStep(state);
      const norte = state.norte;
      let message;
      if (focus) {
        message = `<b>⏱ ${nudge.intervalMin}min se passou.</b> Você está em <b>"${focus.title}"</b>?`;
      } else if (nextSugg) {
        message = `<b>⏱ Check-in.</b> Próximo passo crítico: <b>"${nextSugg.title}"</b>. Está nele agora?`;
      } else if (norte) {
        message = `<b>⏱ Check-in.</b> Norte: <b>"${norte.objective}"</b> · ${norte.horizon}. O que você fez nos últimos ${nudge.intervalMin}min move o Norte?`;
      } else {
        message = `<b>⏱ Check-in.</b> Você não tem Norte definido. Defina antes de continuar.`;
      }
      setNudgeAlert({ mode: nudge.mode, message, focusNode: focus, nextSugg, norte });
      updateNudge({ lastFiredAt: Date.now() });
    }, [state, updateNudge]);

    // ticker
    useEffect(() => {
      const nudge = state.nudge;
      if (!nudge?.enabled) return;
      const tick = () => {
        if (isInSilentWindow(nudge)) return;
        if (nudgeAlert) return; // já tem alerta aberto
        const now = Date.now();
        const since = nudge.lastFiredAt ? (now - nudge.lastFiredAt) / 60000 : Infinity;
        if (since >= nudge.intervalMin) fireNudge();
      };
      tick(); // verifica imediatamente
      const id = setInterval(tick, 30000); // checa a cada 30s
      return () => clearInterval(id);
    }, [state.nudge, nudgeAlert, fireNudge]);

    const dismissNudge = (action) => {
      // action: 'continue' | 'changed' | 'fugiu' | 'snooze'
      if (action === 'snooze') {
        // adia 10min
        updateNudge({ lastFiredAt: Date.now() - (state.nudge.intervalMin - 10) * 60000 });
      } else if (action === 'fugiu') {
        pushMessage({ role:'agent', text:'Honestidade anotada. Sem julgamento. <b>Volta pro Norte.</b> Quer que eu te mostre o próximo passo crítico?', suggestions:[{icon:'→', text:'Mostrar próximo passo'}] });
      } else if (action === 'changed') {
        pushMessage({ role:'agent', text:'Você mudou de nó. Atualiza qual é o foco agora ou aceita ficar sem foco fixo.', suggestions:[{icon:'◎', text:'Avalia se isso te aproxima do Norte'}] });
      }
      setNudgeAlert(null);
    };

    const triggerNextStep = () => {
      const next = findNextStep(state);
      if (!next) { flash(state.norte ? 'Nada crítico em aberto. Caminho fechado ✓' : 'Defina o Norte primeiro'); return; }  setNextStepId(next.id);
      setSelectedId(next.id);
      const rect = canvasWrapRef.current.getBoundingClientRect();
      const targetX = rect.width / 2 - (next.x + 120) * state.zoom;
      const targetY = rect.height / 2 - (next.y + 60) * state.zoom;
      setState(s => ({ ...s, pan: { x: targetX, y: targetY } }));
      flash(`→ Próximo passo: "${next.title}"`);
    };

    const handleSuggestion = (sugText) => {
      const t = sugText.toLowerCase();
      if (t.includes('próximo passo') || t.includes('proximo passo') || t.includes('mostrar próximo')) {
        triggerNextStep();
        return;
      }
      runAgent(sugText);
    };

    // ---- export / import ----
    const doExport = () => {
      const data = JSON.stringify({ nodes: state.nodes, edges: state.edges }, null, 2);
      const blob = new Blob([data], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'fluxo-de-producao.json'; a.click();
      URL.revokeObjectURL(url);
      flash('Exportado como JSON');
    };
    const doImport = () => {
      const inp = document.createElement('input');
      inp.type = 'file'; inp.accept = 'application/json';
      inp.onchange = async (e) => {
        const f = e.target.files[0]; if (!f) return;
        const txt = await f.text();
        try {
          const data = JSON.parse(txt);
          setState(s => ({ ...s, nodes: data.nodes || [], edges: data.edges || [] }));
          flash(`Importado: ${data.nodes?.length || 0} nós`);
        } catch (err) { flash('JSON inválido'); }
      };
      inp.click();
    };
    const doClear = () => {
      if (!confirm('Limpar todo o canvas? Isso não pode ser desfeito.')) return;
      setState(s => ({ ...s, nodes: [], edges: [], messages: [{ role: 'agent', text: 'Canvas zerado. Vamos começar. Arraste um tipo da paleta esquerda, ou me diga o que você tá produzindo agora.', suggestions: [
        { icon: '◆', text: 'Estou fazendo um vídeo do zero ao roteiro' },
        { icon: '◆', text: 'Estou desenvolvendo um produto digital' },
        { icon: '◆', text: 'Tenho vários projetos e preciso priorizar' },
      ] }] }));
      setSelectedId(null);
    };

    // ---- tweaks bridge ----
    useEffect(() => {
      const onMsg = (e) => {
        if (e.data?.type === '__activate_edit_mode') setTweaksOpen(true);
        else if (e.data?.type === '__deactivate_edit_mode') setTweaksOpen(false);
      };
      window.addEventListener('message', onMsg);
      window.parent.postMessage({ type: '__edit_mode_available' }, '*');
      return () => window.removeEventListener('message', onMsg);
    }, []);
    const updateTweak = (k, v) => {
      const next = { ...tweaks, [k]: v };
      setTweaks(next);
      window.parent.postMessage({ type: '__edit_mode_set_keys', edits: { [k]: v } }, '*');
    };

    // ---- counters ----
    const counts = useMemo(() => ({
      total: state.nodes.length,
      doing: state.nodes.filter(n => n.status === 'doing').length,
      done: state.nodes.filter(n => n.status === 'done').length,
      blocked: state.nodes.filter(n => n.status === 'blocked').length,
    }), [state.nodes]);

    // ============ RENDER ============
    const driftAlerts = useMemo(() => detectDrift(state), [state.nodes, state.edges, state.norte]);
    const loopAlerts = useMemo(() => detectLoops(state), [state.nodes, state.edges]);
    const allAlerts = [...driftAlerts, ...loopAlerts];

    return (
      <div className={'shell' + (state.focusMode ? ' focus-mode' : '')}>
        {/* ======= TOP BAR ======= */}
        <div className="topbar">
          <div className="brand">
            <span className="brand-mark"></span>
            <span><b>Fluxo</b> <span style={{opacity:0.4, fontSize:11, marginLeft:6}}>O que move o Norte</span></span>
          </div>
          <button className="tbtn ghost" onClick={() => {
            const rect = canvasWrapRef.current.getBoundingClientRect();
            const x = (rect.width / 2 - state.pan.x) / state.zoom - 120;
            const y = (rect.height / 2 - state.pan.y) / state.zoom - 30;
            addNode('task', { x, y });
          }}>+ Nó <span className="mono" style={{opacity:0.5, marginLeft:4, fontSize:10}}>N</span></button>
          <button className="tbtn primary" onClick={triggerNextStep} title="Encontrar o nó crítico mais executável agora">→ Próximo passo</button>
          <button className="tbtn ghost" onClick={() => setShowImport(true)}>≣ Importar plano MD</button>
          <button className="tbtn ghost" onClick={() => setState(s => ({ ...s, focusMode: !s.focusMode }))} style={state.focusMode ? {borderColor:'var(--amber)', color:'var(--amber)'} : {}}>◇ Foco {state.focusMode ? 'ON' : 'OFF'}</button>
          <button className={'tbtn ghost' + (state.nudge?.enabled ? ' nudge-active' : '')} onClick={() => setShowNudgePanel(v => !v)} title="Cobrança configurável">⏰ Cobrança</button>
          <button className="tbtn ghost" onClick={doImport}>↓ JSON</button>
          <button className="tbtn ghost" onClick={doExport}>↑ JSON</button>
          <button className="tbtn ghost danger" onClick={doClear}>⌫</button>

          <div className="spacer"></div>

          {allAlerts.length > 0 && <div className="stat" style={{borderColor:'var(--danger)', color:'var(--danger)'}}>⚠ <b>{allAlerts.length}</b> alerta{allAlerts.length>1?'s':''}</div>}
          <div className="stat"><span className="dot"></span> <b>{counts.doing}</b> fazendo</div>
          <div className="stat">✓ <b>{counts.done}</b> prontos</div>
          <div className="stat">{counts.blocked > 0 ? '!' : '·'} <b>{counts.blocked}</b> travados</div>
          <div className="stat">∑ <b>{counts.total}</b> nós</div>
        </div>

        {/* ======= NORTE BAR ======= */}
        {state.norte ? (
          <div className="norte-bar">
            <div className="norte-icon"></div>
            <div className="norte-meta">
              <div className="norte-label">Norte · objetivo travado</div>
              <div className="norte-obj">{state.norte.objective}</div>
            </div>
            <div className="norte-horizon">⏱ {state.norte.horizon}</div>
            <div className="norte-dod" onClick={() => setShowNorteEdit(true)} title="Editar Norte e Definition of Done">
              <span className="norte-dod-label">DoD</span>
              <span className="norte-dod-count">{state.norte.dod?.filter(d=>d.done).length || 0}/{state.norte.dod?.length || 0}</span>
              <div className="norte-pips">
                {(state.norte.dod || []).map((d, i) => <div key={i} className={'norte-pip' + (d.done ? ' done' : '')}></div>)}
              </div>
            </div>
          </div>
        ) : (
          <div className="norte-empty">
            <div className="norte-icon" style={{borderColor:'var(--ink-3)', opacity:0.5}}></div>
            <div className="norte-empty-msg">Sem Norte definido. Sem norte, todo trabalho parece urgente. Defina seu objetivo principal agora.</div>
            <button className="tbtn primary" onClick={() => setShowOnboarding(true)}>Definir Norte</button>
          </div>
        )}

        <div className="shell-body">

        {/* ======= LEFT RAIL ======= */}
        <div className="rail">
          <h4>Paleta · arraste p/ canvas</h4>
          <div className="palette">
            {KINDS.map(k => (
              <div key={k.id} className="chip" draggable
                onDragStart={(e) => onPaletteDragStart(e, k.id)}
                onDragEnd={() => setDraggingKind(null)}>
                <span className="dot" style={{ background: k.color }}></span>
                <span className="lbl">{k.label}</span>
              </div>
            ))}
          </div>

          <h4>Legenda · status</h4>
          <div className="legend">
            <div className="legend-row"><span className="pip" style={{borderColor:'var(--ink-3)'}}></span> Parado</div>
            <div className="legend-row"><span className="pip" style={{borderColor:'var(--amber)', background:'radial-gradient(circle, var(--amber) 40%, transparent 42%)'}}></span> Fazendo</div>
            <div className="legend-row"><span className="pip" style={{background:'var(--danger)', borderColor:'var(--danger)'}}></span> Travado</div>
            <div className="legend-row"><span className="pip" style={{background:'var(--lime)', borderColor:'var(--lime)'}}></span> Pronto</div>
          </div>

          <h4>Nós do canvas</h4>
          <div className="rail-list">
            {state.nodes.length === 0 && <div style={{fontSize:11, color:'var(--ink-3)', padding:'8px 6px', fontFamily:'JetBrains Mono'}}>vazio · solte algo aqui</div>}
            {state.nodes.map(n => {
              const k = KINDS.find(x => x.id === n.kind);
              return (
                <div key={n.id} className={'rail-item' + (selectedId === n.id ? ' active' : '')} onClick={() => setSelectedId(n.id)}>
                  <span className="pip" style={{ background: k.color }}></span>
                  <span style={{whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{n.title}</span>
                  <span className="s">{n.status[0].toUpperCase()}</span>
                </div>
              );
            })}
          </div>

          <h4>Atalhos</h4>
          <div style={{fontFamily:'JetBrains Mono', fontSize:10, color:'var(--ink-3)', lineHeight:1.9, padding:'0 6px'}}>
            <div><kbd style={{background:'var(--bg-2)', border:'1px solid var(--line-2)', padding:'1px 5px', borderRadius:3, color:'var(--ink-2)'}}>N</kbd> novo nó</div>
            <div><kbd style={{background:'var(--bg-2)', border:'1px solid var(--line-2)', padding:'1px 5px', borderRadius:3, color:'var(--ink-2)'}}>DEL</kbd> remover</div>
            <div><kbd style={{background:'var(--bg-2)', border:'1px solid var(--line-2)', padding:'1px 5px', borderRadius:3, color:'var(--ink-2)'}}>⌘+scroll</kbd> zoom</div>
            <div>· clique nas bordas p/ conectar</div>
            <div>· arraste no vazio p/ pan</div>
          </div>
        </div>

        {/* ======= CANVAS ======= */}
        <div
          ref={canvasWrapRef}
          className="canvas-wrap"
          onMouseDown={onCanvasMouseDown}
          onWheel={onWheel}
          onDragOver={onCanvasDragOver}
          onDrop={onCanvasDrop}
          onContextMenu={onCanvasContextMenu}
        >
          {tweaks.showGrid !== false && <>
            <div className="canvas-grid" style={{ backgroundPosition: `${state.pan.x}px ${state.pan.y}px`, backgroundSize: `${24 * state.zoom}px ${24 * state.zoom}px` }}></div>
            <div className="canvas-grid lg" style={{ backgroundPosition: `${state.pan.x}px ${state.pan.y}px`, backgroundSize: `${120 * state.zoom}px ${120 * state.zoom}px` }}></div>
          </>}

          {state.nodes.length === 0 && (
            <div className="empty-hint">
              <b>Canvas vazio</b>
              Pressione <kbd>N</kbd> para novo nó<br/>
              ou arraste da paleta à esquerda<br/>
              · clique com botão direito para menu ·
            </div>
          )}

          <div className="canvas-inner" style={{ transform: `translate(${state.pan.x}px, ${state.pan.y}px) scale(${state.zoom})` }}>
            {/* edges */}
            <Edges
              state={state}
              connecting={connecting}
              selectedEdgeId={selectedEdgeId}
              onSelectEdge={(id) => { setSelectedEdgeId(id); setSelectedId(null); }}
            />

            {/* nodes */}
            {state.nodes.map(n => (
              <NodeCard
                key={n.id}
                node={(state.focusMode && !n.critical) || n.id === nextStepId
                  ? { ...n, __atenuated: state.focusMode && !n.critical, __nextStep: n.id === nextStepId }
                  : n}
                selected={selectedId === n.id}
                density={tweaks.density}
                onMouseDown={(e) => startNodeDrag(e, n.id)}
                onStartConnect={(e, side) => startConnect(e, n.id, side)}
                onStatusClick={() => updateNode(n.id, (cur) => ({ status: nextStatus(cur.status) }))}
                onToggleCheck={(cid) => updateNode(n.id, (cur) => {
                  const checks = cur.checks.map(c => c.id === cid ? { ...c, done: !c.done } : c);
                  const done = checks.length ? checks.filter(c => c.done).length / checks.length : 0;
                  return { checks, progress: Math.round(done * 100) };
                })}
              />
            ))}
          </div>

          {/* HUD */}
          <div className="canvas-hud">
            <div className="hud-group">
              <button onClick={() => setState(s => ({ ...s, zoom: Math.max(0.4, s.zoom - 0.1) }))}>−</button>
              <div className="zoom-val" style={{padding:'0 8px', display:'flex', alignItems:'center'}}>{Math.round(state.zoom * 100)}%</div>
              <button onClick={() => setState(s => ({ ...s, zoom: Math.min(2.2, s.zoom + 0.1) }))}>+</button>
              <button onClick={() => setState(s => ({ ...s, zoom: 1, pan: { x: 0, y: 0 } }))}>reset</button>
            </div>
            <div className="hud-group">
              <button onClick={() => setTweaksOpen(t => !t)} title="Tweaks">✱</button>
              <button onClick={() => autoLayout(state, setState)} title="Auto layout">◇ layout</button>
            </div>
          </div>

          <div className="canvas-corner">
            canvas · <b>{state.pan.x.toFixed(0)}, {state.pan.y.toFixed(0)}</b> · zoom <b>{Math.round(state.zoom * 100)}%</b>
          </div>
        </div>

        {/* ======= AGENT ======= */}
        <div className="agent">
          <div className="agent-head">
            <div className="agent-avatar"></div>
            <div className="agent-meta">
              <div className="name">{(PERSONAS[tweaks.agentPersona] || PERSONAS.strategic).name}</div>
              <div className="role">{(PERSONAS[tweaks.agentPersona] || PERSONAS.strategic).role}</div>
            </div>
            <div className="agent-status"><span className="dot"></span> online</div>
          </div>

          <div className="agent-tabs">
            <div className={'agent-tab' + (agentTab === 'chat' ? ' active' : '')} onClick={() => setAgentTab('chat')}>Chat</div>
            <div className={'agent-tab' + (agentTab === 'inspect' ? ' active' : '')} onClick={() => setAgentTab('inspect')}>Inspetor{selectedNode ? ' · 1' : ''}</div>
            <div className={'agent-tab' + (agentTab === 'vitals' ? ' active' : '')} onClick={() => setAgentTab('vitals')}>Sinais</div>
          </div>

          {agentTab === 'chat' && (
            <>
              <div className="agent-body">
                {state.messages.map((m, i) => (
                  <Message key={i} msg={m} onSuggestion={handleSuggestion} />
                ))}
                {agentBusy && <div className="msg agent"><div className="msg-role">S · pensando</div><div className="msg-body"><div className="typing"><span></span><span></span><span></span></div></div></div>}
              </div>
              <Composer onSend={runAgent} busy={agentBusy} />
            </>
          )}

          {agentTab === 'inspect' && (
            <div className="agent-body">
              {selectedNode ? (
                <Inspector node={selectedNode} onUpdate={(p) => updateNode(selectedNode.id, p)} onDelete={() => deleteNode(selectedNode.id)} />
              ) : (
                <div style={{color:'var(--ink-3)', fontSize:12, textAlign:'center', padding:20, fontFamily:'JetBrains Mono'}}>
                  selecione um nó para editar<br/><br/>
                  <span style={{opacity:0.6}}>clique em qualquer card<br/>do canvas</span>
                </div>
              )}
            </div>
          )}

          {agentTab === 'vitals' && <Vitals state={state} alerts={allAlerts} />}
        </div>

        </div>

        {/* tweaks floating */}
        <div className={'tweaks' + (tweaksOpen ? ' open' : '')}>
          <h5>Tweaks</h5>
          <div className="row">
            <label>Tema visual</label>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:4, marginTop:4}}>
              {[
                { id:'obsidian', name:'Obsidiana', preview:'#0e0f10', accent:'#f2a438' },
                { id:'aurora',   name:'Aurora',    preview:'#0d0e17', accent:'#a78bfa' },
                { id:'papel',    name:'Papel',     preview:'#f5f0e8', accent:'#c2690a' },
                { id:'neon',     name:'Neon',      preview:'#050507', accent:'#00ff88' },
                { id:'cafe',     name:'Café',      preview:'#1a1208', accent:'#ffb830' },
              ].map(t => (
                <div key={t.id}
                  onClick={() => updateTweak('theme', t.id)}
                  style={{
                    display:'flex', alignItems:'center', gap:6, padding:'6px 8px',
                    border:`1px solid ${tweaks.theme === t.id ? t.accent : 'var(--line-2)'}`,
                    borderRadius:6, cursor:'pointer', background:'var(--bg-2)',
                    fontSize:11, color: tweaks.theme === t.id ? 'var(--ink)' : 'var(--ink-3)',
                  }}>
                  <span style={{width:14, height:14, borderRadius:3, background:t.preview, border:`2px solid ${t.accent}`, flexShrink:0}}></span>
                  {t.name}
                </div>
              ))}
            </div>
          </div>
          <div className="row">
            <label>Persona do agente</label>
            <select value={tweaks.agentPersona} onChange={e => updateTweak('agentPersona', e.target.value)}>
              <option value="strategic">Workflow Architect</option>
              <option value="pragmatic">Execution Coach</option>
              <option value="socratic">Critic Partner</option>
              <option value="motivational">Momentum Keeper</option>
            </select>
          </div>
          <div className="row">
            <label>Densidade</label>
            <select value={tweaks.density} onChange={e => updateTweak('density', e.target.value)}>
              <option value="comfortable">Confortável</option>
              <option value="compact">Compacto</option>
            </select>
          </div>
          <div className="row">
            <label><input type="checkbox" checked={tweaks.showGrid !== false} onChange={e => updateTweak('showGrid', e.target.checked)} /> Mostrar grade</label>
          </div>
          <button className="tbtn" style={{width:'100%'}} onClick={() => setTweaksOpen(false)}>Fechar</button>
        </div>

        {toast && <div className="toast">{toast}</div>}

        {ctxMenu && (
          <>
            <div style={{position:'fixed', inset:0, zIndex:299}} onClick={() => setCtxMenu(null)}></div>
            <div className="ctx" style={{left: ctxMenu.x, top: ctxMenu.y}}>
              {KINDS.map(k => (
                <div key={k.id} className="ctx-item" onClick={() => { addNode(k.id, { x: ctxMenu.cx - 120, y: ctxMenu.cy - 30 }); setCtxMenu(null); }}>
                  <span style={{width:8, height:8, borderRadius:2, background:k.color}}></span>
                  Novo <b style={{color:'var(--ink)'}}>{k.label.toLowerCase()}</b>
                </div>
              ))}
              <div className="ctx-sep"></div>
              <div className="ctx-item" onClick={() => { setState(s => ({ ...s, pan: { x: 0, y: 0 }, zoom: 1 })); setCtxMenu(null); }}>Resetar vista</div>
            </div>
          </>
        )}

        {showOnboarding && (
          <OnboardingModal
            onComplete={(norte) => {
              setState(s => ({ ...s, norte, onboarded: true, messages: [...s.messages, { role:'agent', text:`Norte travado: <strong>${norte.objective}</strong> em ${norte.horizon}. ${norte.dod.length} critérios de "100% feito" definidos. Agora me conta: você tem um plano (.md, texto colado) ou quer que eu sugira o caminho?`, suggestions: [
                { icon:'≣', text:'Importar plano em Markdown' },
                { icon:'✻', text:'Sugerir caminho crítico baseado no Norte' },
                { icon:'+', text:'Vou montar manualmente, mostre por onde começar' },
              ]}]}));
              setShowOnboarding(false);
            }}
            onCancel={() => setShowOnboarding(false)}
            onSeed={() => { setState(s => ({ ...seedPLX(), planId: s.planId })); setShowOnboarding(false); flash('Seed PLX carregado'); }}
          />
        )}

        {showImport && (
          <ImportModal
            onCancel={() => setShowImport(false)}
            onImport={(parsed, replace) => {
              setState(s => {
                const baseNodes = replace ? [] : s.nodes;
                const baseEdges = replace ? [] : s.edges;
                return { ...s, nodes: [...baseNodes, ...parsed.nodes], edges: [...baseEdges, ...parsed.edges] };
              });
              setShowImport(false);
              flash(`Importado: ${parsed.nodes.length} nós, ${parsed.edges.length} conexões`);
            }}
          />
        )}

        {showNorteEdit && state.norte && (
          <NorteEditModal
            norte={state.norte}
            onSave={(norte) => { setState(s => ({ ...s, norte })); setShowNorteEdit(false); }}
            onCancel={() => setShowNorteEdit(false)}
            onClear={() => { if(confirm('Remover Norte?')) { setState(s => ({ ...s, norte: null })); setShowNorteEdit(false); } }}
          />
        )}

        {completionPrompt && (() => {
          const node = state.nodes.find(n => n.id === completionPrompt.nodeId);
          if (!node) return null;
          const close = () => setCompletionPrompt(null);
          const confirm = () => {
            setState(s => ({ ...s, nodes: s.nodes.map(n => n.id === node.id ? { ...n, status: 'done', progress: 100 } : n) }));
            // limpar nextStep se for esse
            if (nextStepId === node.id) setNextStepId(null);
            // mensagem do agente
            pushMessage({ role: 'agent', text: `✓ <strong>"${node.title}"</strong> fechado. ${node.critical ? 'Caminho crítico avançou. ' : ''}Próximo passo te aguarda.`, suggestions: [
              { icon: '→', text: 'Mostrar próximo passo' },
              { icon: '◎', text: 'Ainda estou no Norte? Avalia.' },
            ]});
            close();
          };
          const back = () => {
            // desmarca último DoD pra forçar revisão
            setState(s => ({ ...s, nodes: s.nodes.map(n => {
              if (n.id !== node.id) return n;
              const dod = [...(n.dod||[])];
              for (let i = dod.length - 1; i >= 0; i--) {
                if (dod[i].done) { dod[i] = { ...dod[i], done: false }; break; }
              }
              return { ...n, dod };
            })}));
            close();
          };
          return (
            <div className="modal-backdrop" onClick={close}>
              <div className="modal" onClick={(e) => e.stopPropagation()} style={{maxWidth: 480}}>
                <div className="modal-head">
                  <div className="modal-kicker">Confirmação · DoD 100%</div>
                  <h2 className="modal-title">Marca como Pronto?</h2>
                  <p className="modal-sub">Você fechou todos os {node.dod?.length} critérios de <strong>"{node.title}"</strong>. Confirma que está realmente pronto, ou voltou rápido demais?</p>
                </div>
                <div className="modal-body">
                  <div style={{display:'flex', flexDirection:'column', gap:6}}>
                    {(node.dod||[]).map((d, i) => (
                      <div key={i} style={{display:'flex', alignItems:'center', gap:8, fontSize:13, color:'var(--lime)'}}>
                        <span style={{width:14, height:14, border:'1.5px solid var(--lime)', background:'color-mix(in oklab, var(--lime), transparent 80%)', borderRadius:3, display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:10}}>✓</span>
                        <span style={{textDecoration:'line-through', color:'var(--ink-2)'}}>{d.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="modal-foot">
                  <button className="tbtn ghost" onClick={back}>Volta, falta algo</button>
                  <button className="tbtn primary" onClick={confirm}>Sim, está pronto ✓</button>
                </div>
              </div>
            </div>
          );
        })()}

        {showNudgePanel && (
          <div className="nudge-panel" onClick={(e) => e.stopPropagation()}>
            <div className="nudge-panel-head">
              <div className="ico">⏰</div>
              <div>
                <div className="title">Cobrança</div>
                <div className="sub">a cada {state.nudge?.intervalMin || 30}min · {state.nudge?.mode || 'soft'}</div>
              </div>
              <div className="x" onClick={() => setShowNudgePanel(false)}>×</div>
            </div>
            <div className="nudge-body">
              <div className={'nudge-toggle' + (state.nudge?.enabled ? ' on' : '')} onClick={() => updateNudge({ enabled: !state.nudge?.enabled, lastFiredAt: !state.nudge?.enabled ? Date.now() : null })}>
                <div className="sw"></div>
                <div className="lbl">{state.nudge?.enabled ? 'Ativa' : 'Desativada'}</div>
                <div className="meta">{state.nudge?.enabled ? 'ON' : 'OFF'}</div>
              </div>

              <div className="nudge-section">
                <label>Frequência (minutos)</label>
                <div className="freq-row">
                  <input type="number" min="1" max="480" value={state.nudge?.intervalMin || 30} onChange={(e) => updateNudge({ intervalMin: Math.max(1, parseInt(e.target.value)||30) })} />
                  <span style={{fontSize:11, color:'var(--ink-3)', fontFamily:'JetBrains Mono'}}>min</span>
                </div>
                <div className="freq-presets">
                  {[5,10,15,30,60,90,120,240,480].map(m => (
                    <button key={m} className={state.nudge?.intervalMin === m ? 'active' : ''} onClick={() => updateNudge({ intervalMin: m })}>{m<60?m+'m':(m/60)+'h'}</button>
                  ))}
                </div>
              </div>

              <div className="nudge-section">
                <label>Modo</label>
                <div className="mode-options">
                  {[
                    {id:'soft', name:'Suave', desc:'Toast no canto. Ignorável. Pra deep work.'},
                    {id:'firm', name:'Firme', desc:'Modal pequeno. Pausa a tela. Exige resposta.'},
                    {id:'brutal', name:'Brutal', desc:'Modal cheio com Norte + prazo. Confronta drift.'},
                  ].map(m => (
                    <div key={m.id} className={'mode-opt' + (state.nudge?.mode === m.id ? ' active' : '')} onClick={() => updateNudge({ mode: m.id })}>
                      <div className="name">{m.name}</div>
                      <div className="desc">{m.desc}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="nudge-section">
                <label>Janela de silêncio</label>
                <div className="silent-row">
                  não me cobra entre
                  <input type="number" min="0" max="23" value={state.nudge?.silentStart ?? 22} onChange={(e) => updateNudge({ silentStart: Math.max(0, Math.min(23, parseInt(e.target.value)||0)) })} />
                  h e
                  <input type="number" min="0" max="23" value={state.nudge?.silentEnd ?? 9} onChange={(e) => updateNudge({ silentEnd: Math.max(0, Math.min(23, parseInt(e.target.value)||0)) })} />
                  h
                </div>
              </div>

              <div className="nudge-section">
                <label>Foco do dia (opcional)</label>
                <select value={state.nudge?.focusNodeId || ''} onChange={(e) => updateNudge({ focusNodeId: e.target.value || null })} style={{width:'100%', padding:'6px 8px', background:'var(--bg-2)', border:'1px solid var(--line-2)', borderRadius:6, color:'var(--ink)', fontSize:12}}>
                  <option value="">— sem foco fixo (usa próximo passo) —</option>
                  {state.nodes.filter(n => n.status !== 'done').map(n => (
                    <option key={n.id} value={n.id}>{n.critical ? '★ ' : ''}{n.title}</option>
                  ))}
                </select>
              </div>

              <div style={{display:'flex', gap:6}}>
                <button className="tbtn ghost" style={{flex:1, fontSize:11}} onClick={fireNudge} disabled={!state.nudge?.enabled}>testar agora</button>
                <button className="tbtn ghost" style={{flex:1, fontSize:11}} onClick={() => updateNudge({ lastFiredAt: Date.now() })} disabled={!state.nudge?.enabled}>resetar timer</button>
              </div>
            </div>
            <div className="nudge-status">
              {state.nudge?.enabled
                ? (state.nudge.lastFiredAt
                    ? `última: ${new Date(state.nudge.lastFiredAt).toLocaleTimeString('pt-BR',{hour:'2-digit',minute:'2-digit'})} · próxima em ${Math.max(0, Math.round(state.nudge.intervalMin - (Date.now() - state.nudge.lastFiredAt)/60000))}min`
                    : 'aguardando primeiro tick…')
                : 'cobrança desativada'}
            </div>
          </div>
        )}

        {nudgeAlert && nudgeAlert.mode === 'soft' && (
          <div className="nudge-toast">
            <div className="nudge-toast-head">⏱ Cobrança · suave</div>
            <div className="nudge-toast-body" dangerouslySetInnerHTML={{__html: nudgeAlert.message}}></div>
            <div className="nudge-toast-actions">
              <button onClick={() => dismissNudge('snooze')}>+10min</button>
              <button className="primary" onClick={() => dismissNudge('continue')}>OK, sigo</button>
            </div>
          </div>
        )}

        {nudgeAlert && (nudgeAlert.mode === 'firm' || nudgeAlert.mode === 'brutal') && (
          <div className="modal-backdrop">
            <div className="modal" style={{maxWidth: nudgeAlert.mode === 'brutal' ? 560 : 440}}>
              <div className="modal-head">
                <div className="modal-kicker">⏱ Cobrança · {nudgeAlert.mode === 'brutal' ? 'BRUTAL' : 'firme'}</div>
                <h2 className="modal-title">{nudgeAlert.mode === 'brutal' ? 'Para. Respira. Avalia.' : 'Check-in rápido'}</h2>
              </div>
              <div className="modal-body">
                <div className="nudge-toast-body" style={{fontSize:14, lineHeight:1.6}} dangerouslySetInnerHTML={{__html: nudgeAlert.message}}></div>
                {nudgeAlert.mode === 'brutal' && nudgeAlert.norte && (
                  <div style={{marginTop:14, padding:12, border:'1px solid color-mix(in oklab, var(--amber), transparent 60%)', borderRadius:8, background:'color-mix(in oklab, var(--amber), transparent 92%)'}}>
                    <div style={{fontFamily:'JetBrains Mono', fontSize:9, color:'var(--amber)', letterSpacing:'0.15em', textTransform:'uppercase', marginBottom:6}}>🎯 Norte ativo</div>
                    <div style={{fontSize:14, color:'var(--ink)', fontWeight:600, marginBottom:6}}>{nudgeAlert.norte.objective}</div>
                    <div style={{fontSize:11, color:'var(--ink-2)', fontFamily:'JetBrains Mono'}}>prazo: {nudgeAlert.norte.horizon} · DoD {(nudgeAlert.norte.dod||[]).filter(d=>d.done).length}/{(nudgeAlert.norte.dod||[]).length}</div>
                  </div>
                )}
                {nudgeAlert.mode === 'brutal' && (
                  <div style={{marginTop:12, fontSize:13, color:'var(--ink-2)', lineHeight:1.6}}>
                    <b style={{color:'var(--ink)'}}>Pergunta sem dar volta:</b> o que você fez nos últimos {state.nudge.intervalMin} minutos te aproximou disso? Ou você fugiu pra outra coisa?
                  </div>
                )}
              </div>
              <div className="modal-foot">
                <button className="tbtn ghost danger" onClick={() => dismissNudge('fugiu')}>Tô fugindo</button>
                <button className="tbtn ghost" onClick={() => dismissNudge('changed')}>Mudei pra outra</button>
                <button className="tbtn ghost" onClick={() => dismissNudge('snooze')}>+10min</button>
                <button className="tbtn primary" onClick={() => dismissNudge('continue')}>Sigo no foco ✓</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ============ SUB-COMPONENTS ============

  function NodeCard({ node, selected, onMouseDown, onStartConnect, onStatusClick, onToggleCheck, density }) {
    const kind = KINDS.find(k => k.id === node.kind) || KINDS[1];
    const compact = density === 'compact';
    return (
      <div
        className={'node' + (selected ? ' selected' : '') + (node.status === 'done' ? ' done' : '') + (node.critical ? ' critical' : '') + (node.__atenuated ? ' atenuated' : '') + (node.__nextStep ? ' next-step' : '')}
        data-kind={node.kind}
        data-id={node.id}
        style={{ left: node.x, top: node.y, width: compact ? 180 : 220, minWidth: compact ? 180 : 220 }}
        onMouseDown={onMouseDown}
      >
        <div className="node-head">
          <span className="node-kind" style={{ color: kind.color, borderColor: 'color-mix(in oklab, ' + kind.color + ', transparent 60%)' }}>{kind.label}</span>
          <button className="node-status" data-s={node.status} onClick={onStatusClick} title={`Status: ${node.status} · clique p/ avançar`}></button>
        </div>
        <div className="node-body">
          <div className="node-title">{node.title || <span style={{color:'var(--ink-3)'}}>sem título</span>}</div>
          {node.desc && !compact && <div className="node-desc">{node.desc}</div>}
        </div>
        {node.checks?.length > 0 && !compact && (
          <div className="node-checks">
            {node.checks.slice(0, 4).map(c => (
              <div key={c.id} className={'node-check' + (c.done ? ' done' : '')} onClick={(e) => { e.stopPropagation(); onToggleCheck(c.id); }}>
                <span className="box"></span>
                <span>{c.text}</span>
              </div>
            ))}
            {node.checks.length > 4 && <div style={{fontSize:10, color:'var(--ink-3)', fontFamily:'JetBrains Mono', paddingLeft:19}}>+ {node.checks.length - 4} mais</div>}
          </div>
        )}
        <div className="node-foot">
          {node.tag && <span className="node-tag">{node.tag}</span>}
          <div className="node-progress">
            <span>{node.progress}%</span>
            <div className="node-progress-bar"><div className={'node-progress-bar-fill' + (node.progress === 100 ? ' full' : '')} style={{ width: `${node.progress}%` }}></div></div>
          </div>
        </div>
        {['top', 'right', 'bottom', 'left'].map(side => (
          <div key={side} className={`node-handle ${side}`} data-side={side} onMouseDown={(e) => onStartConnect(e, side)}></div>
        ))}
      </div>
    );
  }

  function Edges({ state, connecting, selectedEdgeId, onSelectEdge }) {
    const nodeById = Object.fromEntries(state.nodes.map(n => [n.id, n]));
    return (
      <svg className="edges">
        <defs>
          <marker id="arrow-default" viewBox="0 -5 10 10" refX="8" refY="0" markerWidth="6" markerHeight="6" orient="auto">
            <path d="M0,-5L10,0L0,5" fill="var(--ink-3)" />
          </marker>
          <marker id="arrow-selected" viewBox="0 -5 10 10" refX="8" refY="0" markerWidth="6" markerHeight="6" orient="auto">
            <path d="M0,-5L10,0L0,5" fill="var(--amber)" />
          </marker>
        </defs>
        {state.edges.map(e => {
          const a = nodeById[e.from]; const b = nodeById[e.to];
          if (!a || !b) return null;
          const p1 = handlePoint(a, e.fromSide);
          const p2 = handlePoint(b, e.toSide);
          const d = bezierPath(p1, p2, e.fromSide, e.toSide);
          const sel = selectedEdgeId === e.id;
          return (
            <g key={e.id}>
              <path className="edge-hit" d={d} onClick={() => onSelectEdge(e.id)} />
              <path className={'edge' + (sel ? ' selected' : '')} d={d} markerEnd={sel ? 'url(#arrow-selected)' : 'url(#arrow-default)'} />
            </g>
          );
        })}
        {connecting && connecting.mx !== undefined && (
          <path
            d={bezierPath({ x: connecting.x, y: connecting.y }, { x: connecting.mx, y: connecting.my }, connecting.fromSide, 'left')}
            stroke="var(--amber)" strokeWidth="1.5" strokeDasharray="4 3" fill="none" />
        )}
      </svg>
    );
  }

  function Message({ msg, onSuggestion }) {
    return (
      <div className={'msg ' + msg.role}>
        <div className="msg-role">{msg.role === 'user' ? 'você' : 'S · agent'}</div>
        <div className="msg-body" dangerouslySetInnerHTML={{ __html: msg.text }}></div>
        {msg.suggestions?.length > 0 && (
          <div className="suggestion-group">
            {msg.suggestions.map((s, i) => (
              <button key={i} className="suggestion" onClick={() => onSuggestion(s.text)}>
                <span className="icon">{s.icon}</span>
                <span className="txt">{s.text}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  function Composer({ onSend, busy }) {
    const [text, setText] = useState('');
    const submit = () => { if (!text.trim() || busy) return; onSend(text.trim()); setText(''); };
    return (
      <div className="agent-compose">
        <div className="agent-compose-inner">
          <textarea
            placeholder="Pergunte ao S ex: 'o que faço primeiro?', 'quebra essa tarefa', 'revisa meu fluxo'..."
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
          />
          <div className="agent-compose-row">
            <span className="hint"><kbd>⏎</kbd> enviar · <kbd>⇧⏎</kbd> nova linha</span>
            <button className="send-btn" disabled={busy || !text.trim()} onClick={submit}>Enviar →</button>
          </div>
        </div>
      </div>
    );
  }

  function Inspector({ node, onUpdate, onDelete }) {
    const kind = KINDS.find(k => k.id === node.kind);
    return (
      <div>
        <div className="insp-field">
          <label>Tipo</label>
          <div className="pill-group">
            {KINDS.map(k => (
              <div key={k.id} className={'pill' + (node.kind === k.id ? ' active' : '')} onClick={() => onUpdate({ kind: k.id })}>{k.label}</div>
            ))}
          </div>
        </div>
        <div className="insp-field">
          <label>Título</label>
          <input type="text" value={node.title} onChange={e => onUpdate({ title: e.target.value })} />
        </div>
        <div className="insp-field">
          <label>Descrição</label>
          <textarea value={node.desc} onChange={e => onUpdate({ desc: e.target.value })} />
        </div>
        <div className="insp-grid2">
          <div className="insp-field">
            <label>Status</label>
            <select value={node.status} onChange={e => onUpdate({ status: e.target.value })}>
              {STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <div className="insp-field">
            <label>Tag</label>
            <input type="text" value={node.tag} onChange={e => onUpdate({ tag: e.target.value })} placeholder="ex: design, dev" />
          </div>
        </div>
        <div className="insp-field">
          <label>Progresso · {node.progress}%</label>
          <input type="range" min="0" max="100" step="5" value={node.progress} onChange={e => onUpdate({ progress: parseInt(e.target.value) })} style={{width:'100%'}} />
        </div>
        <div className="insp-field">
          <label>Checkpoints</label>
          <div className="insp-chks">
            {node.checks.map(c => (
              <div key={c.id} className="insp-chk-row">
                <input type="checkbox" checked={c.done} onChange={e => onUpdate({ checks: node.checks.map(x => x.id === c.id ? { ...x, done: e.target.checked } : x) })} />
                <input type="text" value={c.text} onChange={e => onUpdate({ checks: node.checks.map(x => x.id === c.id ? { ...x, text: e.target.value } : x) })} />
                <button className="del" onClick={() => onUpdate({ checks: node.checks.filter(x => x.id !== c.id) })}>×</button>
              </div>
            ))}
            <button className="insp-add" onClick={() => onUpdate({ checks: [...node.checks, { id: 'c' + Date.now().toString(36), text: '', done: false }] })}>+ adicionar checkpoint</button>
          </div>
        </div>
        <div className="insp-field">
          <label>Critérios "100% feito" · DoD <span style={{color:'var(--ink-3)', fontWeight:400}}>· {(node.dod||[]).filter(d=>d.done).length}/{(node.dod||[]).length}</span></label>
          <div className="insp-chks">
            {(node.dod || []).map((d, i) => (
              <div key={i} className="insp-chk-row">
                <input type="checkbox" checked={d.done} onChange={e => onUpdate({ dod: (node.dod||[]).map((x, j) => j===i ? { ...x, done: e.target.checked } : x) })} />
                <input type="text" value={d.text} onChange={e => onUpdate({ dod: (node.dod||[]).map((x, j) => j===i ? { ...x, text: e.target.value } : x) })} placeholder="critério objetivo" />
                <button className="del" onClick={() => onUpdate({ dod: (node.dod||[]).filter((_,j) => j!==i) })}>×</button>
              </div>
            ))}
            <button className="insp-add" onClick={() => onUpdate({ dod: [...(node.dod||[]), { text: '', done: false }] })}>+ critério de pronto</button>
          </div>
        </div>
        <div className="insp-field">
          <label style={{display:'flex', alignItems:'center', gap:8, cursor:'pointer'}}>
            <input type="checkbox" checked={!!node.critical} onChange={e => onUpdate({ critical: e.target.checked })} />
            <span>★ Caminho crítico (essencial p/ o Norte)</span>
          </label>
        </div>
        <button className="tbtn danger" style={{width:'100%', marginTop:6}} onClick={onDelete}>⌫ Remover nó</button>
      </div>
    );
  }

  function Vitals({ state, alerts }) {
    const byKind = KINDS.map(k => ({ ...k, count: state.nodes.filter(n => n.kind === k.id).length }));
    const byStatus = STATUSES.map(s => ({ ...s, count: state.nodes.filter(n => n.status === s.id).length }));
    const avgProgress = state.nodes.length ? Math.round(state.nodes.reduce((a,n) => a + n.progress, 0) / state.nodes.length) : 0;
    const blocked = state.nodes.filter(n => n.status === 'blocked');
    const doing = state.nodes.filter(n => n.status === 'doing');
    const critical = state.nodes.filter(n => n.critical);
    const critDone = critical.filter(n => n.status === 'done').length;
    return (
      <div className="agent-body">
        {state.norte && (
          <div style={{padding:12, border:'1px solid color-mix(in oklab, var(--amber), transparent 60%)', borderRadius:8, background:'color-mix(in oklab, var(--amber), transparent 92%)'}}>
            <div style={{fontFamily:'JetBrains Mono', fontSize:9, color:'var(--amber)', letterSpacing:'0.15em', textTransform:'uppercase', marginBottom:6}}>🎯 Norte · DoD</div>
            <div style={{fontSize:13, color:'var(--ink)', fontWeight:600, marginBottom:8, lineHeight:1.3}}>{state.norte.objective}</div>
            <div style={{display:'flex', flexDirection:'column', gap:4}}>
              {(state.norte.dod||[]).map((d,i) => (
                <div key={i} style={{display:'flex', alignItems:'center', gap:6, fontSize:12, color: d.done ? 'var(--lime)' : 'var(--ink-2)'}}>
                  <span style={{width:12, height:12, border:'1.5px solid '+(d.done?'var(--lime)':'var(--ink-3)'), borderRadius:3, display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:9}}>{d.done?'✓':''}</span>
                  <span style={{textDecoration: d.done ? 'line-through' : 'none'}}>{d.text}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {alerts && alerts.length > 0 && (
          <div style={{display:'flex', flexDirection:'column', gap:6}}>
            <div style={{fontFamily:'JetBrains Mono', fontSize:10, color:'var(--danger)', letterSpacing:'0.12em', textTransform:'uppercase'}}>⚠ Radar · {alerts.length} alerta{alerts.length>1?'s':''}</div>
            {alerts.map((a, i) => (
              <div key={i} className={'alert-callout ' + (a.kind==='loop' ? 'danger' : 'warn')} dangerouslySetInnerHTML={{__html: a.text}}></div>
            ))}
          </div>
        )}

        {critical.length > 0 && (
          <div style={{padding:10, border:'1px solid var(--line-2)', borderRadius:8, background:'var(--bg-2)'}}>
            <div style={{fontFamily:'JetBrains Mono', fontSize:10, color:'var(--ink-3)', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:8}}>★ Caminho crítico · {critDone}/{critical.length}</div>
            <div style={{height:3, background:'var(--line-2)', borderRadius:2, overflow:'hidden'}}>
              <div style={{width:`${critical.length ? (critDone/critical.length*100) : 0}%`, height:'100%', background:'var(--amber)'}}></div>
            </div>
          </div>
        )}

        <div style={{padding:10, border:'1px solid var(--line-2)', borderRadius:8, background:'var(--bg-2)'}}>
          <div style={{fontFamily:'JetBrains Mono', fontSize:10, color:'var(--ink-3)', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:8}}>Progresso médio</div>
          <div style={{fontFamily:'JetBrains Mono', fontSize:28, color:'var(--ink)', fontWeight:600}}>{avgProgress}<span style={{fontSize:16, color:'var(--ink-3)'}}>%</span></div>
          <div style={{height:3, background:'var(--line-2)', borderRadius:2, marginTop:8, overflow:'hidden'}}>
            <div style={{width:`${avgProgress}%`, height:'100%', background:avgProgress===100?'var(--lime)':'var(--amber)'}}></div>
          </div>
        </div>

        <div style={{padding:10, border:'1px solid var(--line-2)', borderRadius:8, background:'var(--bg-2)'}}>
          <div style={{fontFamily:'JetBrains Mono', fontSize:10, color:'var(--ink-3)', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:8}}>Por tipo</div>
          {byKind.map(k => (
            <div key={k.id} style={{display:'flex', alignItems:'center', gap:8, marginBottom:4, fontSize:12}}>
              <span style={{width:8, height:8, borderRadius:2, background:k.color}}></span>
              <span style={{color:'var(--ink-2)'}}>{k.label}</span>
              <span style={{marginLeft:'auto', fontFamily:'JetBrains Mono', color:'var(--ink)', fontWeight:600}}>{k.count}</span>
            </div>
          ))}
        </div>

        <div style={{padding:10, border:'1px solid var(--line-2)', borderRadius:8, background:'var(--bg-2)'}}>
          <div style={{fontFamily:'JetBrains Mono', fontSize:10, color:'var(--ink-3)', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:8}}>Por status</div>
          {byStatus.map(s => (
            <div key={s.id} style={{display:'flex', alignItems:'center', gap:8, marginBottom:4, fontSize:12}}>
              <span style={{color:'var(--ink-2)'}}>{s.label}</span>
              <span style={{marginLeft:'auto', fontFamily:'JetBrains Mono', color:'var(--ink)', fontWeight:600}}>{s.count}</span>
            </div>
          ))}
        </div>

        {doing.length > 0 && (
          <div style={{padding:10, border:'1px solid color-mix(in oklab, var(--amber), transparent 70%)', borderRadius:8, background:'color-mix(in oklab, var(--amber), transparent 92%)'}}>
            <div style={{fontFamily:'JetBrains Mono', fontSize:10, color:'var(--amber)', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:8}}>Em andamento · {doing.length}</div>
            {doing.map(n => <div key={n.id} style={{fontSize:12, color:'var(--ink)', marginBottom:3}}>· {n.title}</div>)}
          </div>
        )}

        {blocked.length > 0 && (
          <div style={{padding:10, border:'1px solid color-mix(in oklab, var(--danger), transparent 70%)', borderRadius:8, background:'color-mix(in oklab, var(--danger), transparent 92%)'}}>
            <div style={{fontFamily:'JetBrains Mono', fontSize:10, color:'var(--danger)', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:8}}>Travados · {blocked.length}</div>
            {blocked.map(n => <div key={n.id} style={{fontSize:12, color:'var(--ink)', marginBottom:3}}>! {n.title}</div>)}
          </div>
        )}
      </div>
    );
  }

  // ============ HELPERS ============

  function handlePoint(node, side) {
    const w = 240, h = estimateHeight(node);
    if (side === 'top') return { x: node.x + w/2, y: node.y };
    if (side === 'bottom') return { x: node.x + w/2, y: node.y + h };
    if (side === 'left') return { x: node.x, y: node.y + h/2 };
    return { x: node.x + w, y: node.y + h/2 }; // right
  }
  function estimateHeight(node) {
    let h = 90;
    if (node.desc) h += 20;
    if (node.checks?.length) h += Math.min(node.checks.length, 4) * 18 + 8;
    if (node.tag || node.progress) h += 4;
    return h;
  }
  function bezierPath(p1, p2, s1, s2) {
    const dx = p2.x - p1.x, dy = p2.y - p1.y;
    const dist = Math.hypot(dx, dy);
    const off = Math.min(120, Math.max(40, dist * 0.4));
    const c1 = offsetPoint(p1, s1, off);
    const c2 = offsetPoint(p2, s2, off);
    return `M${p1.x},${p1.y} C${c1.x},${c1.y} ${c2.x},${c2.y} ${p2.x},${p2.y}`;
  }
  function offsetPoint(p, side, d) {
    if (side === 'top')    return { x: p.x, y: p.y - d };
    if (side === 'bottom') return { x: p.x, y: p.y + d };
    if (side === 'left')   return { x: p.x - d, y: p.y };
    return { x: p.x + d, y: p.y };
  }
  function nextStatus(s) {
    const order = ['idle', 'doing', 'done', 'blocked'];
    const i = order.indexOf(s);
    return order[(i + 1) % order.length];
  }

  // ============ MODALS ============

  function OnboardingModal({ onComplete, onCancel, onSeed }) {
    const [stage, setStage] = useState(0);
    const [objective, setObjective] = useState('');
    const [horizon, setHorizon] = useState('30 dias');
    const [dod, setDod] = useState([{ text: '', done: false }]);

    const next = () => setStage(s => s + 1);
    const back = () => setStage(s => Math.max(0, s - 1));
    const finish = () => {
      const cleanDod = dod.filter(d => d.text.trim()).map(d => ({ text: d.text.trim(), done: false }));
      onComplete({ objective: objective.trim(), horizon: horizon.trim(), dod: cleanDod, createdAt: Date.now() });
    };

    return (
      <div className="modal-backdrop" onClick={onCancel}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-head">
            <div className="modal-kicker">Onboarding · 3 perguntas</div>
            <h2 className="modal-title">Antes de fluir, defina o Norte.</h2>
            <p className="modal-sub">Sem objetivo travado, todo trabalho parece urgente e nada vira comercializável. Vamos fixar 3 coisas: <strong>onde</strong>, <strong>quando</strong>, e o que significa <strong>"100% feito"</strong>.</p>
          </div>
          <div className="modal-body">
            <div className="stage-dots">
              {[0,1,2].map(i => <div key={i} className={'stage-dot' + (i === stage ? ' active' : i < stage ? ' done' : '')}></div>)}
            </div>

            {stage === 0 && (
              <>
                <div className="field-group">
                  <label>Pergunta 1 · Qual seu Norte agora?</label>
                  <input type="text" value={objective} onChange={(e) => setObjective(e.target.value)} placeholder='ex: "Primeiro R$297 MRR via WhatsApp SaaS"' autoFocus />
                  <div className="field-hint">Resultado concreto. Comercializável. 1 frase. Não é "ficar famoso" é "10 clientes pagando R$X".</div>
                </div>
                <div style={{borderTop:'1px solid var(--line)', paddingTop:14, marginTop:14}}>
                  <div className="field-hint" style={{marginBottom:8}}>Ou pula tudo e usa o seed:</div>
                  <button className="big-option" onClick={onSeed}>
                    <div className="bo-title">⚡ Carregar plano PLX Empire (WhatsApp SaaS 30d)</div>
                    <div className="bo-desc">Norte + 4 milestones + 7 tarefas pré-mapeadas. Edite depois.</div>
                  </button>
                </div>
              </>
            )}

            {stage === 1 && (
              <div className="field-group">
                <label>Pergunta 2 · Em quanto tempo?</label>
                <input type="text" value={horizon} onChange={(e) => setHorizon(e.target.value)} placeholder="30 dias / 90 dias / Q1 2026" autoFocus />
                <div className="field-hint">Prazo curto força corte. 30-90 dias é o sweet spot pra MRR inicial.</div>
              </div>
            )}

            {stage === 2 && (
              <div className="field-group">
                <label>Pergunta 3 · O que significa "100% feito"?</label>
                <div className="field-hint" style={{marginBottom:10}}>Lista de critérios objetivos. "Feito" não é vibe é checklist. Min. 2 itens, max. 6.</div>
                <div className="dod-list">
                  {dod.map((d, i) => (
                    <div key={i} className="dod-row">
                      <span className="num">{(i+1).toString().padStart(2,'0')}</span>
                      <input type="text" value={d.text} onChange={(e) => setDod(arr => arr.map((x,j) => j===i ? {...x, text:e.target.value} : x))} placeholder='ex: "1 cliente pagante R$297"' />
                      <button className="del" onClick={() => setDod(arr => arr.filter((_,j) => j!==i))}>×</button>
                    </div>
                  ))}
                </div>
                {dod.length < 6 && <button className="tbtn ghost" style={{marginTop:8}} onClick={() => setDod(arr => [...arr, {text:'', done:false}])}>+ critério</button>}
              </div>
            )}
          </div>
          <div className="modal-foot">
            {stage > 0 && <button className="tbtn ghost" onClick={back}>← voltar</button>}
            <button className="tbtn ghost" onClick={onCancel}>cancelar</button>
            {stage < 2 ? (
              <button className="tbtn primary" disabled={stage===0 && !objective.trim()} onClick={next}>próximo →</button>
            ) : (
              <button className="tbtn primary" disabled={!dod.some(d => d.text.trim())} onClick={finish}>travar Norte ✓</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  function ImportModal({ onCancel, onImport }) {
    const [text, setText] = useState('');
    const [replace, setReplace] = useState(false);
    const parsed = useMemo(() => text.trim() ? parseMarkdownPlan(text) : null, [text]);

    return (
      <div className="modal-backdrop" onClick={onCancel}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-head">
            <div className="modal-kicker">Importar plano</div>
            <h2 className="modal-title">Cola Markdown, eu monto o fluxo.</h2>
            <p className="modal-sub">Funciona com qualquer plano em MD: <code># Marco</code> vira milestone, <code>## Sub-marco</code> vira marco filho, <code>### Tarefa</code> ou <code>- item</code> viram tarefas, <code>- [ ] check</code> vira critério de "100% feito" no nó anterior.</p>
          </div>
          <div className="modal-body">
            <div className="field-group">
              <label>Markdown do plano</label>
              <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={'# Lançar SaaS em 30d\n\n## Semana 1 · Landing\n- [ ] Copy do hero\n- [ ] Deploy CF Pages\n\n## Semana 2 · Onboarding\n- [ ] Bot saudação'} autoFocus></textarea>
            </div>
            <label style={{display:'flex', alignItems:'center', gap:8, fontSize:13, color:'var(--ink-2)', cursor:'pointer'}}>
              <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} />
              Substituir canvas atual (apaga nós existentes)
            </label>

            {parsed && parsed.nodes.length > 0 && (
              <div className="import-preview">
                <div style={{fontFamily:'JetBrains Mono', fontSize:10, letterSpacing:'0.1em', color:'var(--ink-3)', textTransform:'uppercase', marginBottom:8}}>
                  Preview · {parsed.nodes.length} nós, {parsed.edges.length} conexões
                </div>
                {parsed.nodes.slice(0, 30).map(n => {
                  const k = KINDS.find(x => x.id === n.kind);
                  return (
                    <div key={n.id} className="import-preview-item">
                      <span className="tag" style={{background:'color-mix(in oklab, '+k.color+', transparent 80%)', color:k.color}}>{k.label}</span>
                      <span style={{color:'var(--ink)'}}>{n.title}</span>
                      {n.dod?.length > 0 && <span style={{color:'var(--ink-3)', fontFamily:'JetBrains Mono', fontSize:10}}> · {n.dod.length} DoD</span>}
                    </div>
                  );
                })}
                {parsed.nodes.length > 30 && <div style={{color:'var(--ink-3)', fontSize:11, marginTop:6}}>+ {parsed.nodes.length - 30} mais...</div>}
              </div>
            )}
          </div>
          <div className="modal-foot">
            <button className="tbtn ghost" onClick={onCancel}>cancelar</button>
            <button className="tbtn primary" disabled={!parsed || parsed.nodes.length === 0} onClick={() => onImport(parsed, replace)}>importar →</button>
          </div>
        </div>
      </div>
    );
  }

  function NorteEditModal({ norte, onSave, onCancel, onClear }) {
    const [objective, setObjective] = useState(norte.objective);
    const [horizon, setHorizon] = useState(norte.horizon);
    const [dod, setDod] = useState(norte.dod || []);

    return (
      <div className="modal-backdrop" onClick={onCancel}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-head">
            <div className="modal-kicker">Norte · objetivo travado</div>
            <h2 className="modal-title">Editar Norte e Definition of Done</h2>
          </div>
          <div className="modal-body">
            <div className="field-group">
              <label>Objetivo</label>
              <input type="text" value={objective} onChange={(e) => setObjective(e.target.value)} />
            </div>
            <div className="field-group">
              <label>Prazo</label>
              <input type="text" value={horizon} onChange={(e) => setHorizon(e.target.value)} />
            </div>
            <div className="field-group">
              <label>Critérios de "100% feito"</label>
              <div className="dod-list">
                {dod.map((d, i) => (
                  <div key={i} className="dod-row">
                    <input type="checkbox" checked={d.done} onChange={(e) => setDod(arr => arr.map((x,j) => j===i ? {...x, done:e.target.checked} : x))} style={{width:16, height:16}} />
                    <input type="text" value={d.text} onChange={(e) => setDod(arr => arr.map((x,j) => j===i ? {...x, text:e.target.value} : x))} />
                    <button className="del" onClick={() => setDod(arr => arr.filter((_,j) => j!==i))}>×</button>
                  </div>
                ))}
              </div>
              <button className="tbtn ghost" style={{marginTop:8}} onClick={() => setDod(arr => [...arr, {text:'', done:false}])}>+ critério</button>
            </div>
          </div>
          <div className="modal-foot">
            <button className="tbtn ghost danger" onClick={onClear} style={{marginRight:'auto'}}>remover Norte</button>
            <button className="tbtn ghost" onClick={onCancel}>cancelar</button>
            <button className="tbtn primary" onClick={() => onSave({ ...norte, objective: objective.trim(), horizon: horizon.trim(), dod: dod.filter(d => d.text.trim()) })}>salvar</button>
          </div>
        </div>
      </div>
    );
  }

  function parseAgentReply(raw) {
    // split out SUGESTÕES: line
    const idx = raw.search(/SUGEST[ÕO]ES:/i);
    let text = raw.trim(); let suggestions = [];
    if (idx >= 0) {
      text = raw.slice(0, idx).trim();
      const sLine = raw.slice(idx).replace(/SUGEST[ÕO]ES:/i, '').trim();
      suggestions = sLine.split('|').map(s => s.trim()).filter(Boolean).slice(0, 4).map(s => ({ icon: '→', text: s }));
    }
    // convert newlines to <br> if not already html
    if (!/<\w+/.test(text)) text = text.replace(/\n/g, '<br/>');
    return { text, suggestions };
  }

  function summarizeState(state) {
    const norte = state.norte;
    let head = '';
    if (norte) {
      const dodDone = norte.dod?.filter(d=>d.done).length || 0;
      const dodTot = norte.dod?.length || 0;
      head = `🎯 NORTE: "${norte.objective}" · prazo ${norte.horizon} · DoD ${dodDone}/${dodTot}\n${norte.dod?.map(d=>`  ${d.done?'[x]':'[ ]'} ${d.text}`).join('\n')||''}\n\n`;
    } else {
      head = '🎯 NORTE: NÃO DEFINIDO usuário ainda não fixou objetivo principal.\n\n';
    }
    if (state.nodes.length === 0) return head + 'Canvas vazio nenhum nó ainda.';
    const critNodes = state.nodes.filter(n => n.critical);
    const offPath = state.nodes.filter(n => !n.critical);
    const lines = state.nodes.map(n => {
      const dodTxt = n.dod?.length ? ` · DoD ${n.dod.filter(d=>d.done).length}/${n.dod.length}` : '';
      const flag = n.critical ? '★' : '·';
      return `${flag} [${n.kind}/${n.status}] "${n.title}"${dodTxt}${n.desc ? ' ' + n.desc.slice(0,80) : ''}`;
    });
    const edgeLines = state.edges.map(e => {
      const a = state.nodes.find(n => n.id === e.from)?.title;
      const b = state.nodes.find(n => n.id === e.to)?.title;
      return `  "${a}" → "${b}"`;
    });
    return `${head}NÓS (★ = caminho crítico, ${critNodes.length} crít / ${offPath.length} fora-de-caminho):\n${lines.join('\n')}\n\nCONEXÕES:\n${edgeLines.join('\n') || '  (nenhuma)'}`;
  }

  // ============ NEXT STEP RESOLVER ============
  // Encontra o nó crítico mais "raso" (folha executável) que ainda não está pronto
  // e cujas dependências estão todas resolvidas.
  function findNextStep(state) {
    const critical = state.nodes.filter(n => n.critical && n.status !== 'done');
    if (critical.length === 0) return null;

    // adjacência: edge.from depende de edge.to (semântica do projeto: filhos apontam para pais/marcos)
    // então, "pré-requisitos de X" = nós Y onde existe edge Y->X (alguém que aponta pra X)
    // mas no nosso modelo: tarefa aponta pra milestone (filho->pai). Então marco "tem como deps" os filhos.
    // Para "executar", queremos a FOLHA (sem nada apontando pra ela ainda aberto).
    const incoming = {}; // quem aponta pra mim
    state.nodes.forEach(n => incoming[n.id] = []);
    state.edges.forEach(e => { if (incoming[e.to]) incoming[e.to].push(e.from); });

    const isLeafReady = (n) => {
      const children = incoming[n.id] || [];
      const openChildren = children.filter(cid => {
        const child = state.nodes.find(x => x.id === cid);
        return child && child.status !== 'done';
      });
      return openChildren.length === 0;
    };

    const leaves = critical.filter(isLeafReady);
    if (leaves.length === 0) {
      // todo crítico tem deps abertas pega o mais raso entre os abertos
      return critical.sort((a,b) => (incoming[a.id]?.length||0) - (incoming[b.id]?.length||0))[0];
    }

    // desempate: 1) status doing > idle > blocked, 2) menos DoDs faltando, 3) ordem de criação
    const score = (n) => {
      const statusBonus = n.status === 'doing' ? 0 : n.status === 'idle' ? 1 : 2;
      const dodOpen = (n.dod || []).filter(d => !d.done).length;
      return statusBonus * 100 + dodOpen;
    };
    return leaves.sort((a,b) => score(a) - score(b))[0];
  }

  // ============ DRIFT & LOOP DETECTION ============
  function detectDrift(state) {
    if (!state.norte) return [];
    const alerts = [];
    const recent = state.nodes.slice(-5);
    const recentOffPath = recent.filter(n => !n.critical && n.status !== 'done');
    if (recentOffPath.length >= 3) {
      alerts.push({ kind: 'drift', text: `<b>Drift detectado.</b> Últimos ${recentOffPath.length} nós criados estão fora do caminho crítico (${recentOffPath.map(n=>`"${n.title}"`).join(', ')}). Risco de afastar do Norte.` });
    }
    const orphans = state.nodes.filter(n => {
      const hasIn = state.edges.some(e => e.to === n.id);
      const hasOut = state.edges.some(e => e.from === n.id);
      return !hasIn && !hasOut && n.id !== 'nt';
    });
    if (orphans.length >= 2) {
      alerts.push({ kind: 'drift', text: `<b>${orphans.length} nós órfãos</b> sem conexão ao fluxo: ${orphans.slice(0,3).map(n=>`"${n.title}"`).join(', ')}. Conecte ao Norte ou arquive.` });
    }
    return alerts;
  }

  function detectLoops(state) {
    const alerts = [];
    // duplicate-ish titles
    const seen = new Map();
    state.nodes.forEach(n => {
      const k = n.title.toLowerCase().replace(/[^a-z0-9]/g,'').slice(0,20);
      if (k.length < 4) return;
      if (!seen.has(k)) seen.set(k, []);
      seen.get(k).push(n);
    });
    seen.forEach((arr) => {
      if (arr.length >= 2) {
        alerts.push({ kind: 'loop', text: `<b>Possível loop.</b> ${arr.length} nós com nomes parecidos: ${arr.map(n=>`"${n.title}"`).join(' / ')}. Você está re-iniciando algo já em andamento?` });
      }
    });
    // cycles in edges
    const adj = {};
    state.edges.forEach(e => { (adj[e.from] ||= []).push(e.to); });
    const visited = new Set(), stack = new Set();
    const dfs = (id) => {
      if (stack.has(id)) return true;
      if (visited.has(id)) return false;
      visited.add(id); stack.add(id);
      for (const next of (adj[id]||[])) if (dfs(next)) return true;
      stack.delete(id); return false;
    };
    for (const n of state.nodes) {
      if (dfs(n.id)) { alerts.push({ kind: 'loop', text: '<b>Ciclo no grafo.</b> Existe uma dependência circular entre nós. Isso trava progresso.' }); break; }
    }
    return alerts;
  }

  // ============ MARKDOWN PARSER ============
  function parseMarkdownPlan(md) {
    const lines = md.split('\n');
    const nodes = [];
    const edges = [];
    let yCol = {};
    let lastH1 = null, lastH2 = null;
    let nodeId = 1;
    const mk = (kind, title, desc, depth) => {
      const id = 'p' + (nodeId++);
      const col = depth;
      yCol[col] = (yCol[col] || 0) + 1;
      nodes.push({
        id, kind, title: title.slice(0, 80), desc: desc.slice(0, 240),
        x: 120 + col * 360, y: 80 + (yCol[col]-1) * 160,
        status: 'idle', tag: '', progress: 0, checks: [], dod: [], critical: depth <= 1,
      });
      return id;
    };
    for (let i = 0; i < lines.length; i++) {
      const ln = lines[i].trim();
      if (!ln) continue;
      let m;
      if ((m = ln.match(/^#\s+(.+)$/))) {
        const id = mk('milestone', m[1].replace(/^[🎯★◆●·\-\*\s]+/,''), '', 0);
        lastH1 = id; lastH2 = null;
      } else if ((m = ln.match(/^##\s+(.+)$/))) {
        const id = mk('milestone', m[1].replace(/^[🎯★◆●·\-\*\s]+/,''), '', 1);
        lastH2 = id;
        if (lastH1) edges.push({ id:'pe'+(edges.length+1), from: id, to: lastH1, fromSide:'top', toSide:'bottom' });
      } else if ((m = ln.match(/^###\s+(.+)$/))) {
        const id = mk('task', m[1].replace(/^[🎯★◆●·\-\*\s]+/,''), '', 2);
        const parent = lastH2 || lastH1;
        if (parent) edges.push({ id:'pe'+(edges.length+1), from: id, to: parent, fromSide:'top', toSide:'bottom' });
      } else if ((m = ln.match(/^[\-\*]\s+\[(.)?\]\s*(.+)$/))) {
        // checkbox item DoD on last node
        const last = nodes[nodes.length-1];
        if (last) (last.dod ||= []).push({ text: m[2].slice(0,80), done: m[1] === 'x' || m[1] === 'X' });
      } else if ((m = ln.match(/^[\-\*]\s+(.+)$/))) {
        if (m[1].length < 100 && m[1].length > 3) {
          const id = mk('task', m[1], '', 2);
          const parent = lastH2 || lastH1;
          if (parent) edges.push({ id:'pe'+(edges.length+1), from: id, to: parent, fromSide:'top', toSide:'bottom' });
        }
      }
    }
    return { nodes, edges };
  }

  function autoLayout(state, setState) {
    // simple columnar layout by rank (incoming edges depth)
    const rank = {};
    const incoming = {};
    state.nodes.forEach(n => { rank[n.id] = 0; incoming[n.id] = []; });
    state.edges.forEach(e => { incoming[e.to]?.push(e.from); });
    // iterative
    for (let i = 0; i < 10; i++) {
      state.nodes.forEach(n => {
        const parents = incoming[n.id] || [];
        if (parents.length) rank[n.id] = Math.max(...parents.map(p => rank[p] + 1));
      });
    }
    const byRank = {};
    state.nodes.forEach(n => { (byRank[rank[n.id]] ||= []).push(n); });
    const laidOut = state.nodes.map(n => {
      const col = rank[n.id];
      const siblings = byRank[col];
      const row = siblings.indexOf(n);
      return { ...n, x: 120 + col * 320, y: 120 + row * 200 };
    });
    setState(s => ({ ...s, nodes: laidOut, pan: { x: 0, y: 0 }, zoom: 1 }));
  }


export default App;
